/**
 * SEO utility functions for generating dynamic metadata
 */

/**
 * Generates a meta description from article content
 * Prioritizes: metaDescription > excerpt > first paragraph
 * Truncates to ~155 characters for SEO best practices
 */
export function generateMetaDescription(
  metaDescription?: string,
  excerpt?: string,
  content?: { type: string; text: string }[]
): string {
  // Priority 1: Use explicit metaDescription if available
  if (metaDescription) {
    return truncateDescription(metaDescription);
  }

  // Priority 2: Use excerpt if available
  if (excerpt) {
    return truncateDescription(excerpt);
  }

  // Priority 3: Extract from first paragraph of content
  if (content && content.length > 0) {
    const firstParagraph = content.find(item => item.type === "paragraph" && item.text);
    if (firstParagraph) {
      // Strip HTML tags if present
      const cleanText = stripHtml(firstParagraph.text);
      return truncateDescription(cleanText);
    }
  }

  // Fallback: generic description
  return "Découvrez nos conseils et stratégies de marketing de contenu pour les entreprises B2B au Québec.";
}

/**
 * Truncates text to ~155 characters for optimal SEO
 * Breaks at word boundary and adds ellipsis if truncated
 */
export function truncateDescription(text: string, maxLength: number = 155): string {
  if (!text) return "";
  
  // Strip HTML tags first
  const cleanText = stripHtml(text);
  
  if (cleanText.length <= maxLength) {
    return cleanText;
  }

  // Find the last space before maxLength to avoid cutting words
  const truncated = cleanText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  
  if (lastSpace > maxLength - 30) {
    return truncated.substring(0, lastSpace) + "…";
  }
  
  return truncated + "…";
}

/**
 * Strips HTML tags from a string
 */
export function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

/**
 * Generates a clean page title with site name suffix
 */
export function generatePageTitle(title: string, suffix: string = "Agence 8020"): string {
  // Ensure title isn't too long (keep under 60 chars ideally)
  const maxTitleLength = 50;
  const cleanTitle = title.length > maxTitleLength 
    ? title.substring(0, maxTitleLength - 3) + "…"
    : title;
    
  return `${cleanTitle} | ${suffix}`;
}

/**
 * Generates a canonical URL for articles
 */
export function generateCanonicalUrl(slug: string, baseUrl: string = "https://agence8020.com"): string {
  return `${baseUrl}/articles/${slug}`;
}
